<template>
  <div
    :id="props.chartId"
    class="i-line-chart"
  >
    <div class="container"></div>
  </div>
</template>

<script setup lang="ts">
import Chart, { type ChartConfiguration } from "chart.js/auto"
import { onBeforeUnmount, onMounted, reactive, watch } from "vue"
import { debounce } from "@/composables/debounce"

// DATA

const chart = reactive({
  container: null as HTMLElement | null,
  element: null as HTMLElement | null,
  generationDelayInMs: 200,
  isResizing: false,
  isVisible: false,
  resizeDebounceInMs: 200
})

// PROPS
const props = defineProps({
  chartId: {
    type: String,
    required: true
  },
  dataSets: {
    type: Array as () => JobDataSet[],
    required: true
  },
  labels: {
    type: Array as () => string[],
    required: true
  },
  range: {
    type: Object as () => { minimum: number; maximum: number },
    required: true
  },
  steps: {
    type: Number,
    required: true
  }
})

// TYPES

type ChartData = {
  dataSets: JobDataSet[]
  labels: string[]
}

type ChartOptions = {
  range: any
  steps: number
}

type JobDataSet = {
  backgroundColor: string
  borderColor: string
  color: string
  data: number[]
  fill: boolean
  label: string
  pointBackgroundColor: string
  pointBorderColor: string
  pointHoverBackgroundColor: string
  pointHoverBorderColor: string
  radius: number
}

// FUNCTIONS

const missingChartData = (chartData: ChartData) => {
  return chartData.dataSets.length === 0 || chartData.labels.length === 0
}

const missingChartOptions = (chartOptions: ChartOptions) => {
  return Object.keys(chartOptions.range).length === 0 || !chartOptions.steps
}

async function generateLineChart(
  chartCanvas: HTMLCanvasElement | null,
  chartData: ChartData,
  chartOptions: ChartOptions
) {
  if (!chartCanvas || missingChartData(chartData) || missingChartOptions(chartOptions)) return

  const { dataSets, labels } = chartData
  const { range, steps } = chartOptions
  const chartStepSize = (range.maximum - range.minimum) / steps

  const chartConfig = {
    type: "line",
    data: {
      labels: [...labels],
      datasets: [...dataSets]
    },
    options: {
      animations: {
        tension: {
          duration: 1000,
          easing: "linear",
          from: 1,
          to: 0.4,
          loop: false
        }
      },
      elements: {
        line: {
          borderWidth: 3
        },
        point: {
          borderWidth: 3,
          hoverBorderWidth: 10,
          radius: 0
        }
      },
      plugins: {
        legend: {
          display: false
        },
        title: {
          display: false
        },
        tooltip: {
          boxWidth: 14,
          boxHeight: 14,
          boxPadding: 6,
          padding: 10
        }
      },
      scales: {
        y: {
          min: range.minimum,
          max: range.maximum,
          ticks: {
            callback: (value: number) => {
              const formattedValue = value / 1000
              const suffix = formattedValue >= 1000 ? "M" : "K"
              return `$${formattedValue.toFixed()}${suffix}`
            },
            stepSize: chartStepSize
          }
        }
      }
    }
  }

  Chart.defaults.font.size = 12
  new Chart(chartCanvas, chartConfig as ChartConfiguration)
}

const delayedChartGeneration = () => {
  return setTimeout(() => {
    initChartGeneration()
  }, chart.generationDelayInMs)
}

const debouncedChartResizing = debounce(() => {
  if (chart.isResizing) return
  chart.isResizing = true

  setTimeout(() => {
    chart.isResizing = false
    initChartGeneration()
  }, chart.generationDelayInMs)
}, chart.resizeDebounceInMs)

const initChartGeneration = () => {
  if (chart.isVisible) {
    if (!chart.container) return
    chart.container.innerHTML = `<canvas></canvas>`
    const canvas = chart.container.querySelector("canvas")

    const chartData = {
      dataSets: props.dataSets,
      labels: props.labels
    }

    const chartOptions = {
      gridColor: "pink",
      range: props.range,
      steps: props.steps
    }

    generateLineChart(canvas, chartData, chartOptions)
  }
}

// WATCHERS
watch(
  () => props.dataSets, // Watch for changes to props
  () => initChartGeneration() // Re-generate chart when props change
)

watch(
  () => chart.isVisible, // Watch for changes to isVisible
  () => chart.isVisible && initChartGeneration() // Re-generate chart when component is visible
)

// LIFECYCLE HOOKS

onMounted(() => {
  chart.element = document.querySelector(`#${props.chartId}`)
  chart.container = document.querySelector(`#${props.chartId} .container`)
  if (!chart.element || !chart.container) return

  // Create an observer to check if the chart is visible
  const observer = new IntersectionObserver(([entry], observer) => {
    if (!entry.isIntersecting) return
    chart.isVisible = true
    observer.disconnect()
  })

  observer.observe(chart.element)
  window.addEventListener("resize", debouncedChartResizing)
  delayedChartGeneration() // Generate chart when dom is fully loaded
})

onBeforeUnmount(() => {
  window.removeEventListener("resize", debouncedChartResizing)
})
</script>

<style lang="scss" scoped>
.i-line-chart {
  width: 100%;

  .container {
    position: relative;
    width: 100%;
  }
}
</style>
